import { createReducer, on, Action } from '@ngrx/store';
import { EntityState, EntityAdapter, createEntityAdapter } from '@ngrx/entity';
import { ISubscription } from '@care-portals/api-interfaces';
import * as SubscriptionsActions from './subscriptions.actions';

export const SUBSCRIPTIONS_FEATURE_KEY = 'subscriptions';

export interface State extends EntityState<ISubscription> {
  loading: boolean;
  error?: string | null;
  activeSubscriptionId: string | null;
}

export interface SubscriptionsPartialState {
  readonly [SUBSCRIPTIONS_FEATURE_KEY]: State;
}

export function selectSubscriptionId(subscription: ISubscription): string {
  return subscription._id;
}

export const subscriptionsAdapter: EntityAdapter<ISubscription> =
  createEntityAdapter<ISubscription>({ selectId: selectSubscriptionId });

export const initialState: State = subscriptionsAdapter.getInitialState({
  loading: false,
  error: null,
  activeSubscriptionId: null
});

const subscriptionsReducer = createReducer(
  initialState,
  on(SubscriptionsActions.getSubscriptions, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(SubscriptionsActions.getSubscriptionsSuccess, (state, { subscriptions }) =>
    subscriptionsAdapter.setAll(subscriptions, {
      ...state,
      loading: false,
      loaded: true
    })
  ),
  on(SubscriptionsActions.getSubscriptionsFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(SubscriptionsActions.getSubscription, (state, { id }) => ({
    ...state,
    loading: true,
    error: null,
    activeSubscriptionId: id
  })),
  on(SubscriptionsActions.getSubscriptionSuccess, (state, { subscription }) =>
    subscriptionsAdapter.upsertOne(subscription, {
      ...state,
      loading: false,
      error: null
    })
  ),
  on(SubscriptionsActions.getSubscriptionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  })),
  on(SubscriptionsActions.updateSubscription, state => ({
    ...state,
    loading: true,
    error: null
  })),
  on(
    SubscriptionsActions.updateSubscriptionSuccess,
    (state, { subscription }) =>
      subscriptionsAdapter.upsertOne(subscription, {
        ...state,
        loading: false
      })
  ),
  on(SubscriptionsActions.updateSubscriptionFailure, (state, { error }) => ({
    ...state,
    loading: false,
    error
  }))
);

export function reducer(state: State | undefined, action: Action) {
  return subscriptionsReducer(state, action);
}
